/**
 * Production Progress — Live production floor overview
 *
 * Shows real-time status of all sub-sessions for a given day:
 * KPI cards (active stations, boxes packed, output weight, avg output),
 * sortable/filterable table via shared ProductionProgressTable component.
 * Updates via WebSocket + 30s polling fallback.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Activity,
  Package,
  Weight,
  TrendingUp,
} from 'lucide-react';
import DatePickerDropdown from '../../components/DatePickerDropdown';
import productionService from '../../services/productionService';
import { useLanguage } from '../../i18n/LanguageContext';
import { wsService } from '../../services/websocket';
import ProductionProgressTable from '../../components/ProductionProgressTable';

// ── Helpers ───────────────────────────────────────────────

const getLocalDateString = (date: Date = new Date()): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateForDisplay = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${weekday} - ${mm}/${dd}/${d.getFullYear()}`;
};

const shiftDate = (dateStr: string, days: number): string => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return getLocalDateString(d);
};

// ── KPI Card ─────────────────────────────────────────────

function KpiCard({
  icon,
  label,
  value,
  bg,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  bg: string;
}) {
  return (
    <div className={`rounded-lg p-4 ${bg} border border-gray-200 dark:border-gray-700`}>
      <div className="flex items-center gap-2 mb-1">{icon}<span className="text-xs text-gray-500 dark:text-gray-400">{label}</span></div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">
        {value}
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────

const ProductionProgress: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  // dateInputRef removed — DatePickerDropdown handles its own state

  const today = getLocalDateString();
  const [selectedDate, setSelectedDate] = useState(today);

  // Fetch data
  const { data: items = [], isLoading, isFetching } = useQuery(
    ['production-progress', selectedDate],
    () => productionService.getDailyWorkQueue(selectedDate),
    { staleTime: 15000, refetchInterval: 30000, keepPreviousData: true },
  );

  // WebSocket real-time invalidation
  useEffect(() => {
    wsService.connect();
    const handler = () => queryClient.invalidateQueries(['production-progress', selectedDate]);
    wsService.on('production-session-updated', handler);
    wsService.on('station-updated', handler);
    wsService.on('production-session-created', handler);
    wsService.on('box-packed', handler);
    wsService.on('box-cancelled', handler);
    wsService.on('box-deleted', handler);
    wsService.on('pending-label-voided', handler);
    return () => {
      wsService.off('production-session-updated', handler);
      wsService.off('station-updated', handler);
      wsService.off('production-session-created', handler);
      wsService.off('box-packed', handler);
      wsService.off('box-cancelled', handler);
      wsService.off('box-deleted', handler);
      wsService.off('pending-label-voided', handler);
    };
  }, [selectedDate, queryClient]);

  // KPI calculations
  const kpis = useMemo(() => {
    const activeStations = new Set(
      items.filter((i) => i.status === 'active' || i.status === 'paused').map((i) => i.station_id),
    );
    const totalBoxes = items.reduce((sum, i) => sum + i.boxes_produced, 0);
    const totalWeight = items.reduce((sum, i) => sum + (i.actual_output_weight ?? 0), 0);
    const outputItems = items.filter((i) => (i.actual_output_weight ?? 0) > 0);
    const avgOutput = outputItems.length > 0
      ? outputItems.reduce((sum, i) => sum + (i.actual_output_weight ?? 0), 0) / outputItems.length
      : 0;
    return {
      activeStations: activeStations.size,
      totalBoxes,
      totalWeight,
      avgOutput,
    };
  }, [items]);

  return (
    <div className="space-y-4">
      {/* Date Navigator */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="grid grid-cols-3 items-center">
          <div className="justify-self-start">
            <button
              onClick={() => setSelectedDate(today)}
              className="px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 border border-indigo-300 dark:border-indigo-600 transition-colors"
            >
              {t('dailyWorkQueue.today')}
            </button>
          </div>

          <div className="justify-self-center flex items-center gap-3">
            <button
              onClick={() => setSelectedDate((d) => shiftDate(d, -1))}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <DatePickerDropdown
              selected={selectedDate}
              onSelect={(date) => setSelectedDate(date)}
            >
              {({ onClick }) => (
                <button
                  type="button"
                  onClick={onClick}
                  className="text-base md:text-lg font-semibold min-w-[180px] md:min-w-[220px] text-center cursor-pointer text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  {formatDateForDisplay(selectedDate)}
                </button>
              )}
            </DatePickerDropdown>
            <button
              onClick={() => setSelectedDate((d) => shiftDate(d, 1))}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="justify-self-end">
            <button
              onClick={() => queryClient.invalidateQueries(['production-progress', selectedDate])}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
              title={t('common.refresh')}
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={<Activity className="w-5 h-5 text-green-600 dark:text-green-400" />}
          label={t('productionProgress.activeStations')}
          value={kpis.activeStations}
          bg="bg-green-50 dark:bg-green-900/20"
        />
        <KpiCard
          icon={<Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
          label={t('productionProgress.boxesPacked')}
          value={kpis.totalBoxes}
          bg="bg-blue-50 dark:bg-blue-900/20"
        />
        <KpiCard
          icon={<Weight className="w-5 h-5 text-purple-600 dark:text-purple-400" />}
          label={t('productionProgress.totalWeight')}
          value={`${kpis.totalWeight.toFixed(2)} lbs`}
          bg="bg-purple-50 dark:bg-purple-900/20"
        />
        <KpiCard
          icon={<TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />}
          label={t('productionProgress.avgOutput')}
          value={kpis.avgOutput > 0 ? `${kpis.avgOutput.toFixed(2)} lbs` : '—'}
          bg="bg-amber-50 dark:bg-amber-900/20"
        />
      </div>

      {/* Shared Table with filters + sorting */}
      <ProductionProgressTable items={items} isLoading={isLoading} />
    </div>
  );
};

export default ProductionProgress;
